import Link from 'next/link';
import { type ComponentProps, memo, type ReactNode, useCallback } from 'react';

import { type UseTrackSidebarInteractionParams, useTrackSidebarInteraction } from '@air/analytics';
import { type SearchIcon } from '@air/design-icons';

import { MainSideNavItemButton } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemButton';
import { MainSideNavItemIcon } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemIcon';
import { MainSideNavItemLabel } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemLabel';

export type MainSideNavIcon = typeof SearchIcon;

type MainSideNavItemIconSize = ComponentProps<MainSideNavIcon>['size'];

export type MainSideNavSidebarInteractionType = UseTrackSidebarInteractionParams['type'];

export type MainSideNavItemProps = {
  href: string;
  icon: MainSideNavIcon;
  iconSize?: MainSideNavItemIconSize;
  id: string;
  isSelected: boolean;
  label: ReactNode;
  sidebarInteractionType?: MainSideNavSidebarInteractionType;
} & Pick<ComponentProps<typeof Link>, 'rel' | 'target'>;

export const MainSideNavItem = memo(
  ({
    href,
    icon: Icon,
    iconSize = 'sm',
    id,
    isSelected,
    label,
    rel,
    sidebarInteractionType,
    target,
  }: MainSideNavItemProps) => {
    const { trackSidebarInteraction } = useTrackSidebarInteraction();

    const handleClick = useCallback(() => {
      if (sidebarInteractionType) {
        trackSidebarInteraction({ action: 'link_click', type: sidebarInteractionType });
      }
    }, [sidebarInteractionType, trackSidebarInteraction]);

    return (
      <MainSideNavItemButton asChild data-item-id={id} data-selected={isSelected} data-selfclose>
        <Link href={href} onClick={handleClick} rel={rel} target={target}>
          <MainSideNavItemIcon>
            <Icon className="shrink-0" size={iconSize} />
          </MainSideNavItemIcon>
          <MainSideNavItemLabel>{label}</MainSideNavItemLabel>
        </Link>
      </MainSideNavItemButton>
    );
  },
);

MainSideNavItem.displayName = 'MainSideNavItem';
